import { useEffect, useState } from "react";
import type { FC } from "react";
import PhoneInput, {
  parsePhoneNumber,
  isValidPhoneNumber,
} from "react-phone-number-input";
import "react-phone-number-input/style.css";
import tw from "tailwind-styled-components";

export interface CustomPhoneInputProps {
  register: any;
  isEdit?: boolean;
  objData?: any;
  t: (key: string) => string;
  errors: Record<string, any>;
  clearErrors: (field: string) => void;
  setError: (field: string, error: { message: string }) => void;
  setValue: (field: string, value: any) => void;
  fieldName: string;
  placeholder?: string;
  disabled?: boolean;
  isRequired?: boolean;
  isDarkMode?: boolean;
}

const CustomPhoneInput: FC<CustomPhoneInputProps> = ({
  register,
  isEdit = false,
  objData,
  t,
  errors,
  clearErrors,
  setError,
  setValue,
  fieldName,
  placeholder,
  disabled = false,
  isRequired = true,
  isDarkMode = false,
}) => {
  const [phoneWithAreaCode, setPhoneWithAreaCode] = useState<string>("");
  const [touched, setTouched] = useState(false);

  // console.log(objData);

  // Helper para armar el valor internacional
  function getInternationalValue(obj: any) {
    if (!obj || !obj.phone) return "";
    const areaCodeNumber = obj.phone?.areaCodeNumber || "";
    const phone = obj.phone?.number?.toString() || "";
    return areaCodeNumber && phone ? `${areaCodeNumber}${phone}` : "";
  }

  function handlePhoneNumberChange(phoneNumber: string | undefined) {
    setPhoneWithAreaCode(phoneNumber || "");

    if (typeof phoneNumber === "string" && isValidPhoneNumber(phoneNumber)) {
      const parsedPhoneNumber = parsePhoneNumber(phoneNumber);
      setValue(fieldName, parsedPhoneNumber?.nationalNumber);
      setValue("areaCodeNumber", `+${parsedPhoneNumber?.countryCallingCode}`);
      setValue("areaCode", parsedPhoneNumber?.country);
    }
  }

  useEffect(() => {
    if (isEdit && objData && objData.phone) {
      setValue(fieldName, objData.phone?.number?.toString() || "");
      setValue("areaCodeNumber", objData.phone?.areaCodeNumber || "");
      setValue("areaCode", objData.phone?.areaCode || "");
      // Setea el valor internacional para el input
      setPhoneWithAreaCode(getInternationalValue(objData));
    }
  }, [isEdit, objData, setValue, fieldName]);

  return (
    <ColumnInput>
      <PhoneInput
        {...register(
          fieldName,
          isRequired
            ? {
                required: t("requiredField"),
              }
            : {}
        )}
        defaultCountry={
          isEdit
            ? objData?.phone?.areaCode
              ? objData.phone.areaCode
              : "MX"
            : "MX"
        }
        id={fieldName}
        name={fieldName}
        disabled={disabled}
        value={phoneWithAreaCode}
        countryCallingCodeEditable={false}
        onFocus={() => setTouched(true)}
        onChange={(e) => {
          setTouched(true);
          handlePhoneNumberChange(e);

          if (!e) {
            setError(fieldName, { message: t("requiredField") });
            return;
          }

          if (!isValidPhoneNumber(e)) {
            setError(fieldName, { message: t("invalidPhone") });
            return;
          }

          clearErrors(fieldName);
        }}
        placeholder={placeholder}
        onBlur={() => {
          if (touched && !phoneWithAreaCode) {
            setError(fieldName, {
              message: t("requiredField"),
            });
          } else if (touched && !isValidPhoneNumber(phoneWithAreaCode)) {
            setError(fieldName, {
              message: t("invalidPhone"),
            });
          } else {
            clearErrors(fieldName);
          }
        }}
        className={`my-phone-input ${isDarkMode ? "dark" : ""} ${
          errors[fieldName]
            ? "border-red-800 focus:border-red-800"
            : "hover:border-blue-500 focus:border-blue-500"
        }`}
      />
    </ColumnInput>
  );
};

export default CustomPhoneInput;

const ColumnInput = tw.div`
flex flex-col gap-1 ml-2
`;
